import { Injectable } from '@angular/core';
import { stopStream } from 'rev-shared/webrtc/streamUtil';
import { debounce } from 'underscore';
import { of } from 'rxjs';
import { IProducerStreamsCfg } from '../../Contract';
import { ProducerService } from '../../Producer.Service';
import { audioQueryConstraints, videoQueryConstraints } from 'rev-capture/Capture.Constants';

@Injectable({
	providedIn: 'root'
})
export class ProducerControlsService {

	constructor(
		private readonly ProducerService: ProducerService
	) {}

	public startCamera(device?: MediaDeviceInfo): Promise<void> {

		return this.getUserMedia({
			video: getConstraint(device, videoQueryConstraints)
		})
			.then(stream => {
				const streams = this.ProducerService.streams;
				stopStream(this.ProducerService.streams.cameraStream);
				streams.cameraStreamProcessor?.destroy();
				const processor = this.ProducerService.getStreamProcessor(stream);

				this.updateStreams({
					cameraDevice: device,
					cameraStream: stream,
					cameraOutputStream$: processor?.outputStream$ || of(stream),
					cameraStreamProcessor: processor,
					cameraLabel: stream.getVideoTracks()[0]?.label,
					cameraStarted: true
				});

				stream.getVideoTracks().forEach(t => t.addEventListener('ended', () => {
					console.log('video track ended');
					this.stopCamera(true);
					setTimeout(() => this.tryUpdateCaptureDevices(true), 500);
				}));
			})
			.catch(e => {
				console.error('getUserMedia error: ', e);
				this.stopCamera();
				return Promise.reject(e);
			});
	}

	public stopCamera(clearSelected?: boolean): void {
		const streams = this.ProducerService.streams;
		streams.cameraStreamProcessor?.destroy();
		stopStream(streams.cameraStream);

		this.updateStreams({
			cameraStream: null,
			cameraOutputStream$: null,
			cameraStreamProcessor: null,
			cameraDevice: clearSelected ? null : streams.cameraDevice,
			cameraLabel: clearSelected ? null : streams.cameraLabel
		});
	}

	public toggleMic(enabled: boolean, device: MediaDeviceInfo): void {
		const streams = this.ProducerService.streams;
		if (streams.micStream && streams.micDevice === device) {
			streams.micStream.getAudioTracks()[0].enabled = enabled;
			this.updateStreams({
				micMuted: !enabled
			});
			return;
		}

		if(!enabled) {
			this.stopMic();
			return;
		}

		this.initMic(device);
	}

	public initMic(device?: MediaDeviceInfo): void {
		this.getUserMedia({
			audio: getConstraint(device, audioQueryConstraints),
		})
			.then(stream => {
				const streams = this.ProducerService.streams;
				const isMute = streams.micMuted;
				stopStream(streams.micStream);

				stream.getAudioTracks().forEach(t => t.addEventListener('ended', () => {
					console.log('audio track ended');
					this.stopMic();
					setTimeout(() => this.tryUpdateCaptureDevices(true), 500);
				}));

				if(isMute){
					stream.getAudioTracks()[0].enabled = false;
				}

				this.updateStreams({
					micDevice: device,
					micStream: stream,
					micLabel: stream.getAudioTracks()[0]?.label
				});

			})
			.catch(e => {
				console.error('getUserMedia error, audio: ', e);
				this.stopMic();
			});
	}

	public stopMic(): void {
		const streams = this.ProducerService.streams;
		stopStream(streams.micStream);
		this.updateStreams({
			micStream: null,
			micMuted: false,
			micDevice: null,
			micLabel: null
		});
	}

	public startCapture(): void {
		(navigator.mediaDevices as any).getDisplayMedia({
			video: videoQueryConstraints,
			audio: audioQueryConstraints
		})
			.then(stream => {
				this.stopCaptureStream();
				stream.getVideoTracks().forEach(t => t.addEventListener('ended', () => this.stopCapture()));

				const video = document.createElement('video');
				video.muted = true;
				video.autoplay = true;
				video.srcObject = stream;
				video.play();
				video.onresize = () => debounce(() => {
					this.ProducerService.updateLiveRecording();
				}, 500);

				this.updateStreams({
					displayCaptureStream: stream,
					displayCaptureVideo: video
				});
			});
	}

	public stopCapture(): void {
		this.stopCaptureStream();

		this.updateStreams({
			displayCaptureStream: null,
			displayCaptureVideo: null
		});
	}

	private stopCaptureStream(): void {
		stopStream(this.ProducerService.streams.displayCaptureStream);
		this.ProducerService.streams.displayCaptureVideo?.remove();
	}

	private updateStreams(data: Partial<IProducerStreamsCfg>): void {
		this.ProducerService.updateProducerStreams({
			...this.ProducerService.streams,
			...data
		});
	}

	private getUserMedia(constraint: MediaStreamConstraints): Promise<MediaStream> {
		//getCaptureDevices can sometimes fail if getUserMedia has not been called.
		//Needs to be updated before if possible, so calling it from both places.
		this.tryUpdateCaptureDevices(true);
		return navigator.mediaDevices.getUserMedia(constraint).then(stream => {
			this.tryUpdateCaptureDevices(true);
			return stream;
		});
	}

	public tryUpdateCaptureDevices(force?: boolean): void {
		const streams = this.ProducerService.streams;

		if(!streams.mediaDevicesLoaded) {
			this.updateStreams({
				mediaDevicesLoaded: true
			});
		} else if(!force) {
			return;
		}

		navigator.mediaDevices.enumerateDevices()
			.then(devices => {
				const streamData = {
					cameraDevices: devices.filter(d => d.deviceId && d.kind === 'videoinput'),
					micDevices: devices.filter(d => d.deviceId && d.kind === 'audioinput')
				};

				this.updateStreams(streamData);
			})
			.catch(e => {
				console.error('Error Loading Devices: ', e);
			});
	}
}

function getConstraint(device: MediaDeviceInfo, constraints?: any): any {
	return device ? { deviceId: { exact: device.deviceId }, ...constraints } :
		(constraints || true);
}
